const recordDateFormatter = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

/** yyyy-mm-dd of the day in Berlin, from the parts, so no locale's date pattern can reorder it. */
export const berlinCalendarDay = (date: Date): string => {
  const part = Object.fromEntries(recordDateFormatter.formatToParts(date).map(({ type, value }) => [type, value]));
  return `${part.year}-${part.month}-${part.day}`;
};

// Day first, as German dates are written: `05.03.1980` is 5 March, never `new Date`'s US-order 3 May.
const GERMAN_DATE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
// Any other text that starts with day and month (`05.03.80`, `05/03/1980`, `05 03 1980`, `05.03.1980 00:00`) has
// no safe order.
const DAY_MONTH_FIRST = /^\d{1,2}[\s./,-]+\d{1,2}(?!\d)/;

// undefined for a day that does not exist on the calendar (e.g. 31 February), which Date.UTC() would roll over.
const germanCalendarDay = (day: number, month: number, year: number): string | undefined => {
  const asUtc = new Date(Date.UTC(year, month - 1, day));
  const isRealDay = asUtc.getUTCFullYear() === year && asUtc.getUTCMonth() === month - 1 && asUtc.getUTCDate() === day;
  const parts = [String(year).padStart(4, "0"), String(month).padStart(2, "0"), String(day).padStart(2, "0")];
  return isRealDay ? parts.join("-") : undefined;
};

/**
 * yyyy-mm-dd of a stored date as the server's `normalizeBirthday` reads it (both change together), or undefined:
 * stored dates are unchecked, and a value without a safe day has none.
 */
export const storedCalendarDay = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  if (!text) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const germanDate = text.match(GERMAN_DATE);
  if (germanDate) return germanCalendarDay(Number(germanDate[1]), Number(germanDate[2]), Number(germanDate[3]));
  if (DAY_MONTH_FIRST.test(text)) return undefined;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? undefined : berlinCalendarDay(date);
};

/** dd.mm.yyyy of a stored date, or undefined for one without a readable day. */
export const formatRecordDate = (value: unknown): string | undefined => {
  const day = storedCalendarDay(value);
  if (!day) return undefined;
  const [year, month, date] = day.split("-");
  return `${date}.${month}.${year}`;
};
